import { useEffect, useState } from "react"
import { Navigate, useNavigate, useLocation, matchPath } from "react-router-dom"
import { getItem, updateLocalStorageUserInfo } from "../../helpers"
import { useSelector } from "react-redux"
import { UnauthorisedAccess } from "../../components/ErrorPages/UnauthorisedAccess"
import { PageNotFound } from "../../components/ErrorPages/PageNotFound"
import SideMenu from "../../components/SideMenu"
import Box from "@mui/material/Box"
import { headerIcons } from "../../cdnImages"
import {
    faChartPieSimpleCircleDollar,
    faBuilding,
    faChargingStation,
    faCreditCard,
    faMessageExclamation,
    faHeadSideGear,
    faCircleDollar,
    faBoltAuto,
    faCalendarCircleUser,
    faGear,
} from "@fortawesome/pro-regular-svg-icons"
import {
    faChartPieSimpleCircleDollar as faChartPieSimpleCircleDollarActive,
    faBuilding as faBuildingActive,
    faChargingStation as faChargingStationActive,
    faCreditCard as faCreditCardActive,
    faMessageExclamation as faMessageExclamationActive,
    faHeadSideGear as faHeadSideGearActive,
    faCircleDollar as faCircleDollarActive,
    faBoltAuto as faBoltAutoActive,
    faCalendarCircleUser as faCalendarCircleUserActive,
    faGear as faGearActive,
} from "@fortawesome/pro-duotone-svg-icons"
import AppLoader from "../../components/AppLoader"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faTimer } from "@fortawesome/pro-regular-svg-icons"

const getFloatingObj = () => {
    try {
        const raw = process.env.REACT_APP_FLOATING_OBJ
        if (!raw) return null
        const cleaned = raw.replace(/;$/, "").trim()
        const jsonStr = cleaned.replace(/'/g, '"').replace(/([{,])\s*(\w+)\s*:/g, '$1"$2":')
        return JSON.parse(jsonStr)
    } catch {
        return null
    }
}

const floatingObj = getFloatingObj()

const CmsLayout = props => {
    const navigate = useNavigate()
    const location = useLocation()
    const { pathname } = useLocation()

    const isLoggedIn = getItem("isUserLoggedIn")
    const { userInfo, userRoles, homeUrl } = useSelector(store => store.auth)

    const [isPageNotFound, setIsPageNotFound] = useState(null)
    const [heading, setHeading] = useState("")
    const [module, subModule] = location.pathname.split("/").filter(Boolean)

    const navbarMenu = [
        {
            id: "dashboard",
            routeId: "dashboard",
            label: "Dashboard",
            icon: faChartPieSimpleCircleDollar,
            activeIcon: faChartPieSimpleCircleDollarActive,
            children: [
                { id: "overview", name: "overview", label: "Overview", path: "/dashboard/overview" },
                { id: "insights", name: "all-chargers", label: "All Chargers", path: "/dashboard/insights" },
            ],
        },
        {
            id: "company-management",
            routeId: "company-management",
            label: "Company Management",
            icon: faBuilding,
            activeIcon: faBuildingActive,
            children: [
                {
                    id: "company-details",
                    name: "Company Details",
                    label: "Company Details",
                    path: "/company-management/company-details",
                },
                { id: "cpo", name: "CPO", label: "Company", path: "/company-management/cpo" },
                { id: "client", name: "clients", label: "Clients", path: "/company-management/client" },
            ],
        },
        {
            id: "charger-management",
            routeId: "charger-management",
            label: "Charger Management",
            icon: faChargingStation,
            activeIcon: faChargingStationActive,
            children: [
                { id: "overview", name: "overview", label: "Overview", path: "/charger-management/overview" },
                { id: "all-stations", name: "stations", label: "Stations", path: "/charger-management/all-stations" },
                {
                    id: "load-management",
                    name: "Load Management",
                    label: "Load Management",
                    path: "/charger-management/load-management",
                },
                { id: "all-chargers", name: "charger", label: "Chargers", path: "/charger-management/all-chargers" },
                {
                    id: "charging-station-log",
                    name: "charging-station-logs",
                    label: "Charging Station Logs",
                    path: "/charger-management/charging-station-log",
                },
                {
                    id: "charging-station-map",
                    name: "station-map",
                    label: "Charging Station Map",
                    path: "/charger-management/charging-station-map",
                },
            ],
        },
        {
            id: "user-management",
            routeId: "user-management",
            label: "User Management",
            icon: faHeadSideGear,
            activeIcon: faHeadSideGearActive,
            children: [
                { id: "overview", name: "overview", label: "Overview", path: "/user-management/overview" },
                { id: "all-evowners", name: "ev-owners", label: "EV Owners", path: "/user-management/all-evowners" },
                {
                    id: "administrator",
                    name: "admins",
                    label: "Administrators",
                    path: "/user-management/administrator",
                },
            ],
        },
        {
            id: "billings-and-payments",
            routeId: "billing",
            label: "Billings & Payments",
            icon: faCreditCard,
            activeIcon: faCreditCardActive,
            children: [
                { id: "overview", name: "overview", label: "Overview", path: "/billing/overview" },
                {
                    id: "charging-sessions",
                    name: "charging-session",
                    label: "Charging Sessions",
                    path: "/billing/charging-sessions",
                },
                { id: "user-payments", name: "user-payments", label: "User Payments", path: "/billing/user-payments" },
                {
                    id: "company-payments",
                    name: "company-payments",
                    label: "Company Payments",
                    path: "/billing/company-payments",
                },
                {
                    id: "company-payouts",
                    name: "company-payouts",
                    label: "Company Payouts",
                    path: "/billing/company-payouts",
                },
                { id: "license", name: "license", label: "License", path: "/billing/license" },
                {
                    id: "company-invoicing",
                    name: "company-invoicing",
                    label: "Company Invoicing",
                    path: "/billing/company-invoicing",
                },
                { id: "pnl", name: "PnL", label: "PnL", path: "/billing/pnl" },
            ],
        },
        {
            id: "complaints-management",
            routeId: "complaints",
            label: "Complaints Management",
            icon: faMessageExclamation,
            activeIcon: faMessageExclamationActive,
            children: [
                { id: "overview", name: "overview", label: "Overview", path: "/complaints/overview" },
                { id: "all-complaints", name: "complaints", label: "Complaints", path: "/complaints/all-complaints" },
            ],
        },
       
        {
            id: "auto-trigger",
            routeId: "auto-trigger",
            label: "Auto Trigger",
            icon: faBoltAuto,
            activeIcon: faBoltAutoActive,
            children: [{ id: "alerts", name: "auto-trigger", label: "Auto Trigger", path: "/auto-trigger/alerts" }],
        },
        {
            id: "subscription",
            routeId: "subscription",
            label: "Subscription",
            icon: faCircleDollar,
            activeIcon: faCircleDollarActive,
            children: [
                {
                    id: "subscription-details",
                    name: "subscription",
                    label: "Subscription",
                    path: "/subscription/subscription-details",
                },
                {
                    id: "subscription-users",
                    name: "subscription users",
                    label: "Subscription Users",
                    path: "/subscription/subscription-users",
                },
            ],
        },
        {
            id: "reports",
            label: "Reports",
            icon: headerIcons?.reports,
            hidden: true, // This won't show in navbar
            children: [{ id: "reports", name: "Finance Related", label: "Report Dashboard", path: "/reports" }],
        },
        {
            id: "edit-profile",
            label: "Edit Profile",
            icon: headerIcons?.reports,
            hidden: true, // This won't show in navbar
            children: [
                { id: "edit-profile", name: "Edit Profile", label: "Edit Profile", path: "/profile/my-profile" },
            ],
        },
         {
            id: "value-added-services",
            routeId: "vas",
            label: "Value Added Services",
            icon: faCalendarCircleUser,
            activeIcon: faCalendarCircleUserActive,
            children: [
                {
                    id: "reservation",
                    name: "reservation",
                    label: "Reservation",
                    path: "/vas/reservation",
                },
            ],
        },
        {
            id: "configuration",
            routeId: "configuration",
            label: "Configuration",
            icon: faGear,
            activeIcon: faGearActive,
            children: [
                {
                    id: "configuration",
                    name: "aggregated-networks-price-change-control",
                    label: "Aggregated Networks Price Change Control",
                    path: "/configuration",
                },
            ],
        },
    ]

    const internalRoutesRbacMap = [
        {
            path: "/charger-management/charger-profile",
            parent: "/charger-management/all-chargers",
        },
        {
            path: "/charger-management/station-profile",
            parent: "/charger-management/all-stations",
        },
        {
            path: "/charger-management/hub-profile",
            parent: "/charger-management/load-management",
        },
        {
            path: "/billing/client-details",
            parent: "/billing/company-payouts",
        },
        {
            path: "/billing/company-monthly-payouts",
            parent: "/billing/company-payouts",
        },
        {
            path: "/billing/payout-ledger",
            parent: "/billing/company-payouts",
        },
        {
            path: "/billing/charger-wise-settlement",
            parent: "/billing/company-payouts",
        },
        {
            path: "/billing/bulk-actions",
            parent: "/billing/company-payouts",
        },
        {
            path: "/billing/advance-rental-bulk-actions",
            parent: "/billing/company-payouts",
        },
        {
            path: "/billing/station-pnl-detail",
            parent: "/billing/pnl",
        },
        {
            path: "/billing/charger-pnl-detail",
            parent: "/billing/pnl",
        },
        {
            path: "/billing/charger-wise-utlization",
            parent: "/billing/company-payouts",
        },
        {
            path: "/auto-trigger/alerts/preferences",
            parent: "/auto-trigger/alerts",
        },
        {
            path: "/auto-trigger/preferences",
            parent: "/auto-trigger/alerts",
        },
        {
            path: "/profile/my-profile",
            publicAuth: true,
        },
        {
            path: "/charger-management/charger-bulk-actions",
            parent: "/charger-management",
        },
        {
            path: "/charger-management/price-history",
            parent: "/charger-management",
        },

        {
            path: "/company-management/company-profile",
            parent: "/company-management/cpo",
        },
        {
            path: "/company-management/client-profile",
            parent: "/company-management/client",
        },
        {
            path: "/configuration/aggregated-price-control",
            parent: "/configuration",
        },
        {
            path: "/configuration/aggregated-price-control/edit/:id",
            parent: "/configuration",
        },
    ]

    const createNavbarStructure = (navbarMenu, rbac, includeHidden = false) => {
        return navbarMenu
            .filter(module => includeHidden || !module.hidden)
            .map(module => {
                const rbacModule = rbac[module.id]

                if (!rbacModule?.view) return null

                if (module.path) return { ...module }

                const children = module.children.filter(child => {
                    const hasRBAC = rbacModule[child.name]?.view === true

                    return hasRBAC
                })

                if (!children.length) return null
                return { ...module, children }
            })
            .filter(Boolean)
    }

    const internalRoutes = internalRoutesRbacMap.map(r => r.path)

    // get all the paths
    const getAllValidPaths = (modules = [], extraPaths = []) => {
        const navbarPaths = modules?.flatMap(module =>
            module.path ? [module.path] : (module.children?.map(child => child?.path) ?? [])
        ) ?? []

        return [...navbarPaths, ...extraPaths]
    }

    // For RBAC checking (include hidden)
    const navbarStructureForRBAC = createNavbarStructure(navbarMenu, userRoles, true)

    const navbarStructure = createNavbarStructure(navbarMenu, userRoles, false)
    // const getAllPaths = getAllValidPaths(navbarMenu)
    const ALL_PATHS = getAllValidPaths(navbarMenu, internalRoutes)
    const getRbacPaths = getAllValidPaths(navbarStructureForRBAC)
    const isKnownRoute = ALL_PATHS.some(path => matchPath({ path, end: false }, pathname)) || null

    const hasAccess =
        // 1️⃣ Public (logged-in only) routes
        internalRoutesRbacMap.some(r => r.publicAuth && matchPath({ path: r.path, end: false }, pathname)) ||
        // 2️⃣ Direct RBAC match
        getRbacPaths.some(rbacPath => matchPath({ path: rbacPath, end: false }, pathname)) ||
        // 3️⃣ Inherited RBAC
        internalRoutesRbacMap.some(
            ({ path, parent }) =>
                parent &&
                matchPath({ path, end: false }, pathname) &&
                getRbacPaths.some(rbacPath => matchPath({ path: parent, end: false }, rbacPath))
        )

    useEffect(() => {
        const firstChildPath = getRbacPaths?.[0]
        if (!firstChildPath) return

        // redirect if user is on root
        if (location.pathname === "/") {
            updateLocalStorageUserInfo("firstPage", firstChildPath)
            navigate(firstChildPath, { replace: true })
        }
        if (module || subModule) {
            const heading = navbarStructure
                .map(
                    item =>
                        item.routeId === module &&
                        item.children.map(child => {
                            return child.id === subModule && child.label
                        })
                )
                .filter(Boolean)
            setHeading(heading)
        }
    }, [userRoles, location.pathname])

    if (!isLoggedIn) {
        return <Navigate to="/auth" replace />
    }

    if (isKnownRoute === null) {
        return <AppLoader />
    }

    if (!isKnownRoute) {
        return <PageNotFound />
    }

    if (!hasAccess) {
        return <UnauthorisedAccess />
    }

    return (
        <>
            {/* <Header {...{ userInfo, userRoles }} /> */}

            {/* {isPageNotFound === null && <AppLoader />} */}
            {/* {isPageNotFound === true && ( */}
            <Box sx={{ display: "flex", height: "100vh", background: "#fff", position: "relative" }}>
                <SideMenu {...{ navbarStructure, userInfo, userRoles, internalRoutesRbacMap }} />
                <Box
                    sx={{
                        display: "flex",
                        height: "100vh",
                        flexGrow: 1,
                        overflow: "auto",
                        flexDirection: "column",
                        padding: "20px",
                    }}>
                    <div>
                       
                        {floatingObj && pathname.includes(floatingObj.path) && (
                            <Box
                                bgcolor="#FC5735"
                                color="#fff"
                                px={2}
                                py={"4px"}
                                fontSize={14}
                                fontWeight={500}
                                overflow="hidden"
                                whiteSpace="nowrap"
                            >
                                <Box
                                    sx={{
                                        display: "inline-flex",
                                        gap: "8px",
                                        alignItems: "center",
                                        willChange: "transform",
                                        animation: "marquee 25s linear infinite",
                                        "@keyframes marquee": {
                                            "0%": { transform: "translateX(100vw)" },
                                            "100%": { transform: "translateX(-100%)" },
                                        },
                                    }}
                                >
                                    <FontAwesomeIcon icon={faTimer} />
                                    <Box component="span">{floatingObj.msg}</Box>
                                </Box>
                            </Box>
                        )}
                         <h2>{heading}</h2>
                    </div>
                    {props.children}
                </Box>
            </Box>
            {/* )} */}

            {/* {isPageNotFound === false && <UnauthorisedAccess />} */}
        </>
    )
}

export default CmsLayout
